<template>
  <button @click="show = true" class="btn btn-info btn-sm"
          v-if="['owner', 'tech', 'accountant'].includes($store.state.user.role)">
    <i class="fa fa-upload"></i>{{ $t('import') }}
    <b-modal v-model="show" :title="$t('importInvoice')" @close="close" no-close-on-esc no-close-on-backdrop
             no-enforce-focus size="s">
      <div class="form-group">
        <label><b>{{ $t('downloadTemplate') }}</b></label>
        <div>
          <button @click="downloadTemplate" class="btn btn-sm btn-secondary mr-1 mb-2">
            <i class="fa fa-fw fa-lg fa-file-excel-o"></i>invoice-template.xlsx
          </button>
        </div>
      </div>
      <div class="form-group">
        <label><b>{{ $t('selectFile') }}</b></label>
        <b-form-file class="mb-3"
                     v-model="file"
                     :placeholder="$t('fileInputPlaceholder')"
                     :drop-placeholder="$t('fileInputPlaceholder')"
                     :browse-text="$t('selectFile')"
                     :disabled="reading||totalRows>0"
                     accept="application/x-iwork-keynote-sffnumbers,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        ></b-form-file>
      </div>
      <div v-if="reading" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>&nbsp;
        <span>{{ $t('importReadingNotice') }}</span>
      </div>
      <div v-if="importing" class="alert alert-warning">
        <b-spinner small variant="primary"></b-spinner>&nbsp;
        <span>{{ $t('importingNotice') }}</span>
      </div>
      <div class="mb-3" v-if="totalRows">
        <b-progress :max="totalRows">
          <b-progress-bar :value="successCount+errorCount"
                          :label="`${(((successCount + errorCount) / totalRows) * 100).toFixed(0)}%`"></b-progress-bar>
        </b-progress>
        <div class="text-center">{{ successCount + errorCount }}/{{ totalRows }} | {{ $t('error') }}: {{ errorCount }}
          <a @click="downloadErrors" class="btn-link text-primary" href="javascript:">[{{ $t('detail') }}]</a>
        </div>
      </div>
      <template v-slot:modal-footer>
        <button class="btn btn-secondary" @click="cancel" v-if="!reading&&importing">{{ $t('cancel') }}</button>
        <button class="btn btn-primary" @click="reset" v-if="!reading&&!importing&&totalRows>0">Reset</button>
        <button v-if="totalRows===0" class="btn btn-primary" @click="process" :disabled="reading||importing">
          <b-spinner small v-if="reading||importing"></b-spinner>
          {{ $t('import') }}
        </button>
      </template>
    </b-modal>
  </button>
</template>

<script>
import fileReaderStream from 'filereader-stream';
import {saveAs} from 'file-saver'
import Excel from 'exceljs';
import moment from 'moment-timezone';

export default {
  data() {
    return {
      show: false,
      file: null,
      reading: false,
      importing: false,
      totalRows: 0,
      successCount: 0,
      errorCount: 0,
      importErrors: [],
      cancelRequest: false,
    }
  },
  methods: {
    close(evt) {
      if (this.reading || this.importing) {
        evt.preventDefault();
      } else {
        this.reset();
      }
    },
    reset() {
      this.totalRows = 0;
      this.successCount = 0;
      this.errorCount = 0;
      this.importErrors = [];
      this.cancelRequest = false;
      this.reading = false;
      this.importing = false;
      this.file = null;
    },
    cancel() {
      this.cancelRequest = true;
    },
    downloadErrors() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Import errors');
      sheet.columns = [
        {header: this.$t('rowNumber'), key: 'row', width: 15},
        {header: this.$t('errors'), key: 'error', width: 50},
      ];
      for (let err of this.importErrors) {
        let errorMsg = 'Unknown error';
        const error = err.err;
        if (error && error.response) {
          const status = error.response.status;
          if (status === 422 && Array.isArray(error.response.data)) {
            errorMsg = error.response.data
                .map(i => `${i.param}: ${i.msg}`)
                .join(', ');
          } else if (error.response.data && error.response.data.message) {
            errorMsg = error.response.data.message;
          } else {
            errorMsg = `HTTP ${status} Error`;
          }
        } else if (error && error.message) {
          errorMsg = error.message;
        }
        sheet.addRow({
          row: err.row,
          error: errorMsg,
        });
      }
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'invoice.xlsx'
        )
      });
    },
    getItem(headerRow, row) {
      let item = {active: true};
      for (let j = 1; j <= headerRow.cellCount; j++) {
        let key = headerRow.getCell(j).value;
        let value;
        if (row.getCell(j).isHyperlink) {
          value = row.getCell(j).value.text;
        } else {
          value = row.getCell(j).value;
        }
        if (value) {
          if (typeof value === 'string') {
            value = value.trim();
          }
          item[key] = value;
        }
      }
      return item;
    },
    process(evt) {
      evt.preventDefault();
      if (!this.file) {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('importFileRequired'),
          icon: 'error',
        });
        return;
      }
      if (this.file.name.endsWith('.xls') || this.file.name.endsWith('.xlsx')) {
        this.reading = true;
        let readStream = fileReaderStream(this.file);
        const workbook = new Excel.Workbook();
        workbook.xlsx.read(readStream).then(async () => {
          if (workbook.worksheets.length === 0) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let sheet = workbook.worksheets[0];
          if (sheet.actualRowCount < 3) {
            throw new Error(this.$t('fileHasNoData'));
          }
          if (!sheet.getRow(1).values.find(v => v && String(v).trim() === 'domain')) {
            throw new Error(this.$t('invalidFileTemplate'));
          }
          let headerRow = sheet.getRow(1);
          let i = 3;
          this.totalRows = sheet.actualRowCount - 2;
          this.reading = false;
          this.importing = true;
          while (i <= sheet.actualRowCount) {
            if (this.cancelRequest) {
              break;
            }
            let item = this.getItem(headerRow, sheet.getRow(i));
            try {
              if (item.invoiceStartTime) {
                if (typeof (item.invoiceStartTime) === 'string') {
                  let format = 'DD/MM/YYYY'
                  if (String(item.invoiceStartTime).includes(':')) {
                    format = 'DD/MM/YYYY HH:mm:ss'
                  }
                  const m = moment(item.invoiceStartTime, format)
                  if (m.isValid()) {
                    item.invoiceStartTime = m.toDate().getTime()
                  } else {
                    throw new Error('invoiceStartTime: ' + this.$t('invalidType'))
                  }
                } else if (typeof item.invoiceStartTime === 'number') {
                  const date = new Date(item.invoiceStartTime);
                  if (isNaN(date.getTime())) {
                    throw new Error('invoiceStartTime: ' + this.$t('invalidType'))
                  }
                  if (item.invoiceStartTime < 1e12 || item.invoiceStartTime > 1e14) {
                    if (item.invoiceStartTime < 1e12) {
                      item.invoiceStartTime = moment.utc('1899-12-30').add(item.invoiceStartTime, 'days').tz('Asia/Ho_Chi_Minh').format('x');
                    } else if (item.invoiceStartTime > 1e14) {
                      throw new Error('invoiceStartTime: ' + this.$t('invalidType'));
                    } else {
                      throw new Error('invoiceStartTime: ' + this.$t('invalidType'));
                    }
                  }
                } else {
                  item.invoiceStartTime = moment(item.invoiceStartTime.toUTCString().replace(' GMT', '')).tz('Asia/Ho_Chi_Minh').format('x');
                  if (item.invoiceStartTime === 'Invalid date') {
                    throw new Error('invoiceStartTime: ' + this.$t('invalidType'));
                  }
                }
              }
              if (item.invoiceEnabled) {
                if (typeof (item.invoiceEnabled) === 'string') {
                  let check = item.invoiceEnabled.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, "").toLowerCase();
                  if (check === 'co') {
                    item.invoiceEnabled = true;
                  } else if (check === 'khong') {
                    item.invoiceEnabled = false;
                  } else {
                    throw new Error('invoiceEnabled: ' + this.$t('invalidType'))
                  }
                } else {
                  throw new Error('invoiceEnabled: ' + this.$t('invalidType'))
                }
              } else {
                item.invoiceEnabled = false;
              }
              await this.$bgService.put('/invoice-info', item);
              this.successCount++;
            } catch (err) {
              this.importErrors.push({row: i, err: err});
              this.errorCount++;
            }
            i++;
          }
        }).catch(err => {
          this.$swal({
            title: this.$t('error'),
            text: err.message,
            icon: 'error',
          });
        }).finally(() => {
          this.reading = false;
          this.importing = false;
          this.cancelRequest = false;
          this.$bus.$emit('reloadInvoiceInfo');
        })
      } else {
        this.$swal({
          title: this.$t('error'),
          text: this.$t('invalidFileType'),
          icon: 'error',
        });
      }
    },
    downloadTemplate() {
      let workbook = new Excel.Workbook();
      let sheet = workbook.addWorksheet('Invoice Info');
      this.setupSheet(sheet);
      let row = {
        domain: 'Domain',
        invoiceEnabled: this.$t('invoiceEnabled') + ' (' + this.$t('yes') + '/' + this.$t('no') + ')' ,
        invoiceCustomerName: this.$t('invoiceCustomerName'),
        invoiceCompanyName: this.$t('invoiceCompanyName'),
        invoiceTaxCode: this.$t('invoiceTaxCode'),
        invoiceIdentity: this.$t('invoiceIdentity'),
        invoiceAddress: this.$t('invoiceAddress'),
        invoiceEmail: this.$t('invoiceEmail') + ' (' + this.$t('emailNote') + ')',
        invoiceStartTime: this.$t('invoiceStartTime'),
      };
      sheet.addRow(row);
      workbook.xlsx.writeBuffer({base64: true}).then(data => {
        saveAs(
            new Blob([data], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}),
            'invoice-template.xlsx'
        )
      });
    },
    setupSheet(sheet) {
      sheet.columns = [
        {header: 'domain', key: 'domain', width: 50},
        {header: 'invoiceEnabled', key: 'invoiceEnabled', width: 30},
        {header: 'invoiceCustomerName', key: 'invoiceCustomerName', width: 40},
        {header: 'invoiceCompanyName', key: 'invoiceCompanyName', width: 50},
        {header: 'invoiceTaxCode', key: 'invoiceTaxCode', width: 50},
        {header: 'invoiceIdentity', key: 'invoiceIdentity', width: 50},
        {header: 'invoiceAddress', key: 'invoiceAddress', width: 50},
        {header: 'invoiceEmail', key: 'invoiceEmail', width: 70},
        {header: 'invoiceStartTime', key: 'invoiceStartTime', width: 50},
      ]
    },
  },
  i18n: {
    messages: {
      vi: {
        import: 'Tải lên',
        importInvoice: 'Tải lên danh sách hóa đơn',
        downloadTemplate: 'Tải xuống file mẫu',
        selectFile: 'Chọn file',
        fileInputPlaceholder: 'Chọn hoặc kéo thả file',
        importReadingNotice: 'Đang đọc dữ liệu...',
        importingNotice: 'Dữ liệu đang được tải lên...',
        errors: 'Lỗi',
        detail: 'Chi tiết',
        importFileRequired: 'Không được bỏ trống file',
        invalidFileTemplate: 'File sai định dạng',
        invalidFileType: 'Sai loại file',
        invoiceCompanyName: "Tên đơn vị",
        invoiceTaxCode: "Mã số thuế",
        invoiceIdentity: "Số CCCD",
        invoiceAddress: "Địa chỉ",
        invoiceEmail: "Email nhận hóa đơn",
        invoiceStartTime: "Thời gian bắt đầu xuất hóa đơn",
        invoiceEnabled: 'Xuất hóa đơn VAT',
        rowNumber: "Số hàng",
        yes: 'Có',
        no: 'Không',
        invalidType: 'Kiểu dữ liệu không hợp lệ',
        invoiceCustomerName: "Tên người mua hàng",
        emailNote: 'Upload nhiều email cần cách nhau bằng dấu ; '
      },
      en: {
        import: 'Import',
        importInvoice: 'Import Invoice list',
        downloadTemplate: 'Download template',
        selectFile: 'Select file',
        fileInputPlaceholder: 'Choose or drag file',
        importReadingNotice: 'Reading data...',
        importingNotice: 'Uploading data...',
        errors: 'Errors',
        detail: 'Detail',
        importFileRequired: 'File required!',
        invalidFileTemplate: 'Invalid file template',
        invalidFileType: 'Invalid file type',
        invoiceCompanyName: "Invoice Company Name",
        invoiceTaxCode: "Invoice Tax Code",
        invoiceIdentity: "Invoice Identity",
        invoiceAddress: "Invoice Address",
        invoiceEmail: "Invoice Email",
        invoiceStartTime: "Invoice Start Time",
        invoiceEnabled: 'VAT Invoice Enabled',
        rowNumber: 'Row number',
        yes: 'Yes',
        no: 'No',
        invalidType: 'Invalid type',
        invoiceCustomerName: "Invoice Customer Name",
        emailNote: 'Upload multiple emails separated by ; '
      }
    }
  }
}
</script>